import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { TranscodeImageProps, UploadedFile } from "@thalamos/frontend";
import { Base64FileUpload } from "@thalamos/frontend";

const SIGNATURE_HEADER_BYTES = 12;

/**
 * The browser types a file by its name rather than its content, so a renamed
 * file can claim a type it is not. Each check reads the file's leading bytes,
 * decoded to a binary string, for the signature of the type it claims. Checks
 * see only the file's first `SIGNATURE_HEADER_BYTES` bytes.
 */
const SIGNATURE_CHECKS = {
  "application/pdf": (header) => header.startsWith("%PDF-"),
  "image/gif": (header) =>
    header.startsWith("GIF87a") || header.startsWith("GIF89a"),
  "image/jpeg": (header) => header.startsWith("\xff\xd8\xff"),
  "image/png": (header) => header.startsWith("\x89PNG\r\n\x1a\n"),
  "image/webp": (header) =>
    header.startsWith("RIFF") && header.startsWith("WEBP", 8),
} satisfies Record<string, (header: string) => boolean>;

/** A MIME type `Upload` can check a file's content against. */
export type VerifiableMimeType = keyof typeof SIGNATURE_CHECKS;

export type UploadProps = {
  setUploadedFile: (
    callback: (prevUploadedFiles: UploadedFile[] | null) => UploadedFile[],
  ) => void;
  setFileError: (error: string | null) => void;
  labelType?: "form-upload" | "signature";
  icon?: any;
  accept?: string;
  multi?: boolean;
  uploadMultiple?: boolean;
  transcodeImage?: TranscodeImageProps;
  validMimeTypes: readonly VerifiableMimeType[];
  maxFileSizeBytes?: number;
  fileTooLargeErrorText?: string;
  disabled?: boolean;
};

const DEFAULT_MAX_FILE_SIZE_BYTES = 200_000;

function contentMatchesType(
  file: UploadedFile,
  type: VerifiableMimeType,
): boolean {
  if (typeof file.base64 !== "string") {
    throw new Error("File was not read as a data URL");
  }

  // An empty file's data URL has no payload.
  const payload = file.base64.split(",")[1] ?? "";
  // Base64 spells each three bytes as four characters.
  const headerBase64Length = Math.ceil(SIGNATURE_HEADER_BYTES / 3) * 4;
  return SIGNATURE_CHECKS[type](atob(payload.slice(0, headerBase64Length)));
}

export const Upload = ({
  setUploadedFile,
  setFileError,
  labelType,
  icon,
  accept,
  multi,
  uploadMultiple,
  transcodeImage,
  validMimeTypes,
  maxFileSizeBytes,
  fileTooLargeErrorText,
  disabled,
}: UploadProps) => {
  const { t } = useTranslation();

  const onDone = useCallback(
    (files: UploadedFile[]) => {
      let allValid = true;

      for (const file of files) {
        const claimedType = validMimeTypes.find((type) => type === file.type);
        const isValidType =
          claimedType !== undefined && contentMatchesType(file, claimedType);

        if (!isValidType) {
          const validMimeTypesStrings = validMimeTypes
            .map((type) => type.split("/")[1])
            .join(", ");

          const errorMessage = t("pages.common.signature.uploadLabelErrorImg", {
            validMimeTypes: validMimeTypesStrings,
          });

          setFileError(errorMessage);
          allValid = false;
          break;
        }

        // If we are transcoding the image, we don't care about the original file size
        const isImage = file.type.startsWith("image/");
        const isValidSize =
          (transcodeImage !== undefined && isImage) ||
          file.size < (maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES);

        if (!isValidSize) {
          setFileError(
            fileTooLargeErrorText ??
              t("pages.common.signature.uploadLabelErrorFileSize1MB"),
          );
          allValid = false;
          break;
        }
      }

      if (allValid) {
        setFileError(null);

        setUploadedFile((prevUploadedFiles: UploadedFile[] | null) => {
          // TODO: I don't understand why this logic is in here
          if (!multi) {
            return files;
          }

          return [...(prevUploadedFiles ?? []), ...files];
        });
      }
    },
    [
      fileTooLargeErrorText,
      maxFileSizeBytes,
      multi,
      setFileError,
      setUploadedFile,
      t,
      transcodeImage,
      validMimeTypes,
    ],
  );

  return (
    <Base64FileUpload
      disabled={disabled}
      icon={icon}
      transcodeImage={transcodeImage}
      onDone={onDone}
      accept={accept}
      label={
        labelType === "form-upload"
          ? "Select a file"
          : t("components.signatureDialog.uploadLabel")
      }
      multi={uploadMultiple}
    />
  );
};
